import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useToast } from "@repo/ui";

export const AuthErrorHandler = () => {
  const auth = useAuth();
  const { showToast } = useToast();
  const hasShownExpiredToast = useRef(false);

  useEffect(() => {
    const tryRenew = () => {
      if (document.visibilityState === "visible" && auth.user?.expired) {
        void auth.signinSilent().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", tryRenew);
    window.addEventListener("online", tryRenew);
    window.addEventListener("focus", tryRenew);
    return () => {
      document.removeEventListener("visibilitychange", tryRenew);
      window.removeEventListener("online", tryRenew);
      window.removeEventListener("focus", tryRenew);
    };
  }, [auth]);

  useEffect(() => {
    if (!auth.error) return;

    const errorMessage = auth.error.message?.toLowerCase() || "";
    const isTokenError =
      errorMessage.includes("token") ||
      errorMessage.includes("unauthorized") ||
      errorMessage.includes("invalid") ||
      auth.error.name === "TokenExpiredError";

    if (isTokenError && !hasShownExpiredToast.current) {
      hasShownExpiredToast.current = true;
      showToast(
        "error",
        "Your session is invalid. Please sign in again.",
        "Authentication Error",
        0
      );
      void auth.removeUser();
    }
  }, [auth.error, showToast, auth]);

  useEffect(() => {
    if (!auth.events) return;

    const handleTokenExpired = () => {
      console.warn("Access token expired. Waiting for background refresh...");
    };

    const handleSilentRenewError = (error: Error) => {
      console.error("Silent token renewal failed:", error);

      const errorMessage = error.message?.toLowerCase() || "";
      const isNetworkError =
        errorMessage.includes("network") ||
        errorMessage.includes("fetch") ||
        errorMessage.includes("failed to fetch");

      if (isNetworkError) {
        console.warn("Network offline. Keeping session alive to retry later.");
        return;
      }

      if (!hasShownExpiredToast.current) {
        hasShownExpiredToast.current = true;
        showToast(
          "error",
          "Unable to refresh your session. Please sign in again.",
          "Session Refresh Failed",
          0
        );
        void auth.removeUser();
      }
    };

    const handleUserLoaded = () => {
      hasShownExpiredToast.current = false;
    };

    auth.events.addAccessTokenExpired(handleTokenExpired);
    auth.events.addSilentRenewError(handleSilentRenewError);
    auth.events.addUserLoaded(handleUserLoaded);

    return () => {
      auth.events.removeAccessTokenExpired(handleTokenExpired);
      auth.events.removeSilentRenewError(handleSilentRenewError);
      auth.events.removeUserLoaded(handleUserLoaded);
    };
  }, [auth.events, showToast, auth]);

  return null;
};
